import React, { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { useAuth, hasPerm } from "@/context/AuthContext";
import { useResourceList, useResourceMutations } from "@/hooks/useResource";
import { Toolbar } from "@/components/kit/Toolbar";
import { DataTable } from "@/components/kit/DataTable";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { FormDrawer } from "@/components/kit/FormDrawer";
import { TextField, SelectField, SwitchField, TextareaField } from "@/components/kit/fields";

function FieldRenderer({ field, value, onChange }) {
  const t = field.type || "text";
  const tid = `field-${field.key}`;
  if (t === "select") return <SelectField label={field.label} value={value ?? ""} onChange={onChange} options={field.options || []} testId={tid} />;
  if (t === "switch") return <SwitchField label={field.label} hint={field.hint} checked={!!value} onChange={onChange} testId={tid} />;
  if (t === "textarea") return <TextareaField label={field.label} value={value ?? ""} onChange={onChange} testId={tid} />;
  return <TextField label={field.label} type={t === "number" ? "number" : "text"} value={value ?? ""} onChange={onChange} testId={tid} placeholder={field.placeholder} />;
}

/** Generic tenant-scoped CRUD section (table + add/edit Drawer) driven by config. */
export function SettingsResource({ resource, title, addLabel, columns, fields, search = true, idPrefix }) {
  const { user } = useAuth();
  const [searchVal, setSearchVal] = useState("");
  const [drawer, setDrawer] = useState({ open: false, item: null });
  const { data, isLoading } = useResourceList(resource, search && searchVal ? { search: searchVal } : {});
  const { create, update, remove } = useResourceMutations(resource);
  const pfx = idPrefix || resource;

  const rows = data?.items || [];
  const canEdit = hasPerm(user, `${resource}.edit`) || hasPerm(user, `${resource}.create`);

  const rowActions = (row) => [
    { label: "Edit", icon: Pencil, onClick: () => setDrawer({ open: true, item: row }), testId: `edit-${pfx}-${row.id}` },
    ...(hasPerm(user, `${resource}.delete`) ? [{ label: "Delete", icon: Trash2, danger: true,
      onClick: () => window.confirm(`Delete this ${title.toLowerCase()}?`) && remove.mutate(row.id), testId: `delete-${pfx}-${row.id}` }] : []),
  ];

  return (
    <div className="space-y-4" data-testid={`${pfx}-section`}>
      <Toolbar
        search={search ? searchVal : undefined} onSearch={search ? setSearchVal : undefined}
        searchPlaceholder={`Search ${title.toLowerCase()}...`}
        onAdd={hasPerm(user, `${resource}.create`) ? () => setDrawer({ open: true, item: null }) : null}
        addLabel={addLabel} addTestId={`add-${pfx}-btn`} />
      <DataTable testId={`${pfx}-table`} columns={columns} rows={rows} loading={isLoading}
        onRowClick={canEdit ? (r) => setDrawer({ open: true, item: r }) : undefined}
        rowActions={canEdit ? rowActions : null} emptyText={`No ${title.toLowerCase()} yet.`} />
      <ResourceDrawer open={drawer.open} item={drawer.item} fields={fields} title={title} testIdPrefix={pfx}
        onOpenChange={(o) => setDrawer((d) => ({ ...d, open: o }))}
        onSubmit={async (body) => {
          if (drawer.item) await update.mutateAsync({ id: drawer.item.id, body });
          else await create.mutateAsync(body);
          setDrawer({ open: false, item: null });
        }} />
    </div>
  );
}

function ResourceDrawer({ open, item, fields, title, testIdPrefix, onOpenChange, onSubmit }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      const init = {};
      fields.forEach((f) => { init[f.key] = item ? item[f.key] : (f.default ?? (f.type === "switch" ? false : "")); });
      setForm(init);
    }
  }, [open, item]); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    try { await onSubmit(form); } finally { setSaving(false); }
  };

  return (
    <FormDrawer open={open} onOpenChange={onOpenChange}
      title={item ? `Edit ${title}` : `Add ${title}`} size="md"
      testId={`${testIdPrefix}-drawer`}
      onSave={handleSave} saving={saving} saveLabel={item ? "Save" : "Create"}>
      <div className="space-y-4">
        {fields.map((f) => (
          <FieldRenderer key={f.key} field={f} value={form[f.key]} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} />
        ))}
      </div>
    </FormDrawer>
  );
}

export { StatusBadge };
